import React, { useState, useEffect } from "react";
import { supabase } from "../config/supabase";
import { User } from "@supabase/supabase-js";
import { addDataRefreshListener } from "../utils/events";
import { Child } from "../types/child";
import { getGoalForDate, getLocalDateString, getWeeklyGoalTotal } from "../utils/goalUtils";
import CircularProgress from "../components/CircularProgress";

interface StatisticsProps {
  user: User | null;
  darkMode: boolean;
}

interface DailyLog {
  date: string;
  time_spent_minutes: number;
  daily_goal_at_time?: number | null; // Historical goal from when log was created (null = rest day)
  weekly_goal_at_time?: number | null; // Historical goal from when log was created (null = rest day)
}

interface UserGoals {
  weekly_goal_minutes: number;
  monday_goal_minutes?: number | null;
  tuesday_goal_minutes?: number | null;
  wednesday_goal_minutes?: number | null;
  thursday_goal_minutes?: number | null;
  friday_goal_minutes?: number | null;
  saturday_goal_minutes?: number | null;
  sunday_goal_minutes?: number | null;
}

interface ChildStats {
  child_id: string;
  child_name: string;
  total_time: number;
  total_sessions: number;
  average_session_length: number;
  goal_achievement_rate: number;
  consistency_rate: number;
  weekly_goal_minutes: number;
  // Today's goal for this child (null = rest day)
  today_goal_minutes: number | null;
  longest_streak: number;
  current_streak: number;
  best_day: {
    date: string;
    minutes: number;
  };
  weekly_average: number;
  monthly_average: number;
  today_minutes: number;
  weekly_total: number;
  week_daily_minutes: Record<string, number>;
  // New statistics from mobile app
  weekly_average_percentage: number; // Average % of daily goal completion for current week
  goals_hit_this_week: number; // Number of days hitting full daily goal this week
  active_days_this_week: number; // Number of days with any activity this week
}

const Statistics: React.FC<StatisticsProps> = ({ user, darkMode }) => {
  const [weeklyLogs, setWeeklyLogs] = useState<DailyLog[]>([]);
  // Separate state for CURRENT week (always shown in goal cards regardless of navigation)
  const [currentWeekLog, setCurrentWeekLog] = useState<DailyLog | null>(null);
  const [currentWeekLogs, setCurrentWeekLogs] = useState<DailyLog[]>([]);
  const [userGoals, setUserGoals] = useState<UserGoals>({
    weekly_goal_minutes: 0,
  });
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState<Child[]>([]);
  const [treatmentType, setTreatmentType] = useState<"self" | "child">("self");
  const [childStats, setChildStats] = useState<ChildStats[]>([]);
  // Full child goals rows (used for per-date goal calculations, like the mobile app)
  const [childGoalsById, setChildGoalsById] = useState<
    Record<string, UserGoals>
  >({});
  const [selectedChildLogs, setSelectedChildLogs] = useState<DailyLog[]>([]);
  const [localSelectedChildId, setLocalSelectedChildId] = useState<
    string | undefined
  >(undefined);
  const [selectedWeekOffset, setSelectedWeekOffset] = useState(0); // 0 = current week, -1 = previous week, etc.

  useEffect(() => {
    if (user) {
      fetchTreatmentType();
      fetchStatisticsData();
      fetchCurrentWeekData(); // Always fetch current week for goal cards
      fetchUserGoals();
    } else {
      // Clean up state when user becomes null (e.g., during logout)
      setWeeklyLogs([]);
      setCurrentWeekLog(null);
      setCurrentWeekLogs([]);
      setUserGoals({
        weekly_goal_minutes: 0,
      });
      setChildren([]);
      setChildStats([]);
      setChildGoalsById({});
      setSelectedChildLogs([]);
      setLoading(false);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch children only when treatment type is "child", or self stats when "self"
  useEffect(() => {
    if (user && treatmentType === "child") {
      fetchChildren();
    } else if (treatmentType === "self") {
      setChildren([]);
      setChildStats([]);
      setSelectedChildLogs([]);
    }
  }, [user, treatmentType, userGoals]); // Added userGoals to recalculate when goals change

  // React to global refresh events (e.g., settings updated, log added)
  useEffect(() => {
    const remove = addDataRefreshListener(() => {
      if (user) {
        fetchTreatmentType();
        fetchStatisticsData();
        fetchUserGoals();
        if (treatmentType === "child") {
          fetchChildren();
        }
        if (localSelectedChildId) {
          fetchSelectedChildLogs();
        }
      }
    });
    return remove;
  }, [user, localSelectedChildId, treatmentType]);

  // Fetch individual child logs when localSelectedChildId changes
  useEffect(() => {
    if (localSelectedChildId && user) {
      fetchSelectedChildLogs();
    } else {
      setSelectedChildLogs([]);
    }
  }, [localSelectedChildId, selectedWeekOffset, user]);

  // Refetch data when week changes
  useEffect(() => {
    if (user) {
      fetchStatisticsData();
    }
  }, [selectedWeekOffset, user]);

  const fetchSelectedChildLogs = async () => {
    if (!user || !localSelectedChildId) return;

    try {
      // Apply week offset for viewing previous/future weeks
      const weekStart = new Date();
      weekStart.setDate(
        weekStart.getDate() - weekStart.getDay() + selectedWeekOffset * 7,
      );
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      const weekStartStr = getLocalDateString(weekStart);
      const weekEndStr = getLocalDateString(weekEnd);

      const { data, error } = await supabase
        .from("child_logs")
        .select(
          "date, time_spent_minutes, daily_goal_at_time, weekly_goal_at_time",
        )
        .eq("child_id", localSelectedChildId)
        .gte("date", weekStartStr)
        .lte("date", weekEndStr)
        .order("date", { ascending: true });

      if (error) throw error;

      // Group entries by date and sum up the time, keeping historical goal
      const groupedData = new Map<
        string,
        { totalMinutes: number; dailyGoalAtTime?: number | null }
      >();
      (data || []).forEach((entry: any) => {
        const existing = groupedData.get(entry.date);
        const newTotal =
          (existing?.totalMinutes || 0) + entry.time_spent_minutes;
        // Keep first defined goal (preserves null for rest days)
        const goalAtTime =
          existing?.dailyGoalAtTime !== undefined ? existing.dailyGoalAtTime : entry.daily_goal_at_time;
        groupedData.set(entry.date, {
          totalMinutes: newTotal,
          dailyGoalAtTime: goalAtTime,
        });
      });

      // Create DailyLog objects from grouped data
      const childLogs: DailyLog[] = Array.from(groupedData.entries()).map(
        ([date, { totalMinutes, dailyGoalAtTime }]) => ({
          date,
          time_spent_minutes: totalMinutes,
          daily_goal_at_time: dailyGoalAtTime,
        }),
      );

      setSelectedChildLogs(childLogs);
    } catch (error) {
      console.error("Error fetching selected child logs:", error);
    }
  };

  const fetchTreatmentType = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("user_data")
        .select("treatment_type")
        .eq("user_id", user.id)
        .single();

      if (error) throw error;

      setTreatmentType((data?.treatment_type as "self" | "child") || "self");
    } catch (error: any) {
      // Don't log RLS policy errors during logout
      if (error?.code !== "42501") {
        console.error("Error fetching treatment type:", error);
      }
      setTreatmentType("self");
    }
  };

  const fetchChildren = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("children")
        .select("*")
        .eq("user_id", user.id)
        .order("name");

      if (error) throw error;

      setChildren(data || []);

      // If children exist, fetch their statistics
      if (data && data.length > 0) {
        // Always select first child if none selected (required for child treatment type)
        if (!localSelectedChildId) {
          setLocalSelectedChildId(data[0].id);
        }
        // If selected child no longer exists, select first child
        if (
          localSelectedChildId &&
          !data.find((c) => c.id === localSelectedChildId)
        ) {
          setLocalSelectedChildId(data[0].id);
        }
        fetchChildStatistics(data);
      }
    } catch (error) {
      console.error("Error fetching children:", error);
    }
  };

  const fetchChildStatistics = async (childrenList: Child[]) => {
    if (!user) return;

    try {
      const childStatsArray: ChildStats[] = [];
      const nextChildGoalsById: Record<string, UserGoals> = {};

      for (const child of childrenList) {
        // Get child goals
        const { data: goalsData, error: goalsError } = await supabase
          .from("child_goals")
          .select(
            "weekly_goal_minutes, monday_goal_minutes, tuesday_goal_minutes, wednesday_goal_minutes, thursday_goal_minutes, friday_goal_minutes, saturday_goal_minutes, sunday_goal_minutes",
          )
          .eq("child_id", child.id)
          .single();

        // If no row for goals, fall back to defaults; only skip on real errors
        if (goalsError && (goalsError as any).code !== "PGRST116") {
          continue;
        }

        // Store full per-day goal data for later per-date calculations (UI/weekly breakdowns)
        nextChildGoalsById[child.id] = {
          weekly_goal_minutes: (goalsData as any)?.weekly_goal_minutes ?? 0,
          monday_goal_minutes: (goalsData as any)?.monday_goal_minutes,
          tuesday_goal_minutes: (goalsData as any)?.tuesday_goal_minutes,
          wednesday_goal_minutes: (goalsData as any)?.wednesday_goal_minutes,
          thursday_goal_minutes: (goalsData as any)?.thursday_goal_minutes,
          friday_goal_minutes: (goalsData as any)?.friday_goal_minutes,
          saturday_goal_minutes: (goalsData as any)?.saturday_goal_minutes,
          sunday_goal_minutes: (goalsData as any)?.sunday_goal_minutes,
        };

        const todayLocal = getLocalDateString();
        const todayGoalFromDaily = goalsData
          ? getGoalForDate(goalsData as any, todayLocal)
          : null;
        // For UI/timer math: treat rest day (NULL) as 0 minutes goal.
        const todayGoalMinutes = todayGoalFromDaily;
        const weeklyGoal = goalsData
          ? getWeeklyGoalTotal(goalsData as any)
          : 0;

        // Get child logs for the last 30 days
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 30);
        // Also compute today's and this week's ranges
        const todayStr = getLocalDateString(endDate);
        const weekStart = new Date();
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        const weekStartStr = getLocalDateString(weekStart);
        const weekEndStr = getLocalDateString(weekEnd);

        const { data: logsData, error: logsError } = await supabase
          .from("child_logs")
          .select(
            "date, time_spent_minutes, notes, daily_goal_at_time, weekly_goal_at_time",
          )
          .eq("child_id", child.id)
          .gte("date", getLocalDateString(startDate))
          .lte("date", getLocalDateString(endDate))
          .order("date", { ascending: true });

        if (logsError) continue;

        // Calculate statistics
        const totalTime =
          logsData?.reduce((sum, log) => sum + log.time_spent_minutes, 0) || 0;
        const totalSessions = logsData?.length || 0;
        const averageSessionLength =
          totalSessions > 0 ? totalTime / totalSessions : 0;

        // Calculate goal achievement rate
        const daysInPeriod = 30;
        // Use day-specific goals for each day in the period (rest day => 0 minutes goal)
        let totalGoalTime = 0;
        if (goalsData) {
          for (let i = 0; i < daysInPeriod; i++) {
            const d = new Date(endDate);
            d.setDate(endDate.getDate() - i);
            const dStr = getLocalDateString(d);
            const g = getGoalForDate(goalsData as any, dStr);
            totalGoalTime += g === null ? 0 : g;
          }
        }
        const goalAchievementRate =
          totalGoalTime > 0 ? (totalTime / totalGoalTime) * 100 : 0;

        // Calculate consistency rate
        const uniqueDays = new Set(logsData?.map((log) => log.date) || []).size;
        const consistencyRate =
          daysInPeriod > 0 ? (uniqueDays / daysInPeriod) * 100 : 0;

        // Calculate streaks - aggregate by date first
        const dailyTotalsForStreak = new Map<string, number>();
        logsData?.forEach((log) => {
          const current = dailyTotalsForStreak.get(log.date) || 0;
          dailyTotalsForStreak.set(log.date, current + log.time_spent_minutes);
        });

        // Calculate current streak (from today backwards) - minimum 30 minutes
        // Rest days (null goal) don't break the streak - they're treated as successful days
        const MINIMUM_STREAK_MINUTES = 30;
        let currentStreak = 0;
        let checkDate = new Date();

        while (true) {
          const checkDateStr = getLocalDateString(checkDate);
          const dayTotal = dailyTotalsForStreak.get(checkDateStr) || 0;

          // Rest day detection:
          // - Prefer historical goal from logs (daily_goal_at_time === null => rest day)
          // - If no logs, fall back to current configured goals for that date
          const dayLogs =
            logsData?.filter((log) => log.date === checkDateStr) || [];
          let isRestDay = dayLogs.some(
            (log) => log.daily_goal_at_time === null,
          );
          if (!isRestDay && goalsData) {
            const g = getGoalForDate(goalsData as any, checkDateStr);
            isRestDay = g === null;
          }

          // Rest days don't break the streak - continue counting
          // Days with activity >= minimum also continue the streak
          if (isRestDay || dayTotal >= MINIMUM_STREAK_MINUTES) {
            currentStreak++;
            checkDate.setDate(checkDate.getDate() - 1);
          } else {
            break;
          }

          // Prevent infinite loop
          if (currentStreak > 365) break;
        }

        // Calculate longest streak (any period) — rest days don't break it
        let longestStreak = currentStreak;
        if (logsData && logsData.length > 0) {
          const allDates = logsData.map((l) => l.date).sort();
          const walkDate = new Date(allDates[0] + "T00:00:00");
          const todayWalk = new Date();
          todayWalk.setHours(0, 0, 0, 0);
          let tempStreak = 0;

          while (walkDate <= todayWalk) {
            const walkDateStr = getLocalDateString(walkDate);
            const dayTotal = dailyTotalsForStreak.get(walkDateStr) || 0;

            const dayLogs =
              logsData?.filter((log) => log.date === walkDateStr) || [];
            let isRestDay = dayLogs.some(
              (log) => log.daily_goal_at_time === null,
            );
            if (!isRestDay && goalsData) {
              const g = getGoalForDate(goalsData as any, walkDateStr);
              isRestDay = g === null;
            }

            if (isRestDay || dayTotal >= MINIMUM_STREAK_MINUTES) {
              tempStreak++;
              longestStreak = Math.max(longestStreak, tempStreak);
            } else {
              tempStreak = 0;
            }

            walkDate.setDate(walkDate.getDate() + 1);
          }
        }

        // Find best day
        let bestDay = { date: "", minutes: 0 };
        dailyTotalsForStreak.forEach((minutes, date) => {
          if (minutes > bestDay.minutes) {
            bestDay = { date, minutes };
          }
        });

        // Calculate averages
        const weeklyAverage = totalTime / 4; // 4 weeks
        const monthlyAverage = totalTime;

        // Today and weekly totals (current week)
        const todayMinutes = Number(
          logsData
            ?.filter((l) => l.date === todayStr)
            .reduce((sum, l) => sum + (l.time_spent_minutes || 0), 0) ?? 0,
        );
        const weeklyTotal = Number(
          logsData
            ?.filter((l) => l.date >= weekStartStr && l.date <= weekEndStr)
            .reduce((sum, l) => sum + (l.time_spent_minutes || 0), 0) ?? 0,
        );

        // Per-day totals for current week for multi-ring per-day view
        const weekDailyMinutes: Record<string, number> = {};
        (logsData || [])
          .filter((l) => l.date >= weekStartStr && l.date <= weekEndStr)
          .forEach((l) => {
            weekDailyMinutes[l.date] =
              (weekDailyMinutes[l.date] || 0) + (l.time_spent_minutes || 0);
          });

        // NEW STATISTICS FROM MOBILE APP

        // First, aggregate logs by date with historical goals (for current week stats)
        const currentWeekLogsByDate = new Map<
          string,
          { totalMinutes: number; dailyGoalAtTime: number | null }
        >();

        // Get current week's Sunday
        const currentWeekStart = new Date();
        currentWeekStart.setDate(
          currentWeekStart.getDate() - currentWeekStart.getDay(),
        );
        const currentWeekStartStr = getLocalDateString(currentWeekStart);

        // Aggregate current week's logs with historical goals
        (logsData || [])
          .filter((l) => l.date >= currentWeekStartStr && l.date <= todayStr)
          .forEach((l) => {
            const existing = currentWeekLogsByDate.get(l.date);
            const newTotal =
              (existing?.totalMinutes || 0) + (l.time_spent_minutes || 0);
            // Preserve null from historical goal (null = rest day)
            const goalAtTime =
              (l as any).daily_goal_at_time !== undefined
                ? (l as any).daily_goal_at_time
                : (goalsData ? getGoalForDate(goalsData as any, l.date) : null);
            currentWeekLogsByDate.set(l.date, {
              totalMinutes: newTotal,
              dailyGoalAtTime: goalAtTime,
            });
          });

        // 1. Weekly Average % - Average percentage of daily goal completion for current week
        // Uses HISTORICAL goals from each day's logs (like mobile app)
        let totalGoalPercentage = 0;
        let daysInCurrentWeek = 0;

        for (let i = 0; i < 7; i++) {
          const checkDate = new Date(currentWeekStart);
          checkDate.setDate(currentWeekStart.getDate() + i);
          const checkDateStr = getLocalDateString(checkDate);

          // Only count days up to today
          if (checkDateStr <= todayStr) {
            daysInCurrentWeek++;

            const dayData = currentWeekLogsByDate.get(checkDateStr);
            const dayTotal = dayData?.totalMinutes || 0;
            // If log exists, use its historical goal (may be null = rest day); otherwise check current goals
            const dayGoal = dayData !== undefined
              ? dayData.dailyGoalAtTime
              : (goalsData ? getGoalForDate(goalsData as any, checkDateStr) : null);
            const dayPercentage =
              dayGoal === null
                ? 100
                : dayGoal > 0
                  ? (dayTotal / dayGoal) * 100
                  : 0;
            totalGoalPercentage += dayPercentage;
          }
        }
        const weeklyAveragePercentage =
          daysInCurrentWeek > 0
            ? Math.round(totalGoalPercentage / daysInCurrentWeek)
            : 0;

        // 2. Goals Hit This Week - Days that hit full daily goal this week (using HISTORICAL goals)
        let goalsHitThisWeek = 0;
        currentWeekLogsByDate.forEach((dayData) => {
          if (
            dayData.dailyGoalAtTime === null ||
            dayData.totalMinutes >= dayData.dailyGoalAtTime
          ) {
            goalsHitThisWeek++;
          }
        });

        // 3. Active Days This Week - Days with any activity this week
        // Use the aggregated current week data (consistent with weekly average calculation)
        const activeDaysThisWeek = Array.from(
          currentWeekLogsByDate.values(),
        ).filter((dayData) => dayData.totalMinutes > 0).length;

        childStatsArray.push({
          child_id: child.id,
          child_name: child.name,
          total_time: totalTime,
          total_sessions: totalSessions,
          average_session_length: averageSessionLength,
          goal_achievement_rate: goalAchievementRate,
          consistency_rate: consistencyRate,
          weekly_goal_minutes: weeklyGoal,
          today_goal_minutes: todayGoalMinutes,
          longest_streak: longestStreak,
          current_streak: currentStreak,
          best_day: bestDay,
          weekly_average: weeklyAverage,
          monthly_average: monthlyAverage,
          today_minutes: todayMinutes,
          weekly_total: weeklyTotal,
          week_daily_minutes: weekDailyMinutes,
          // New statistics from mobile app
          weekly_average_percentage: weeklyAveragePercentage,
          goals_hit_this_week: goalsHitThisWeek,
          active_days_this_week: activeDaysThisWeek,
        });
      }

      setChildStats(childStatsArray);
      setChildGoalsById(nextChildGoalsById);
    } catch (error) {
      console.error("Error fetching child statistics:", error);
    }
  };

  // Fetch CURRENT week data (always offset 0) - for goal cards that should stay current
  const fetchCurrentWeekData = async () => {
    if (!user) return;

    const today = getLocalDateString();
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // Always current Sunday
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6); // Always current Saturday
    const weekStartStr = getLocalDateString(weekStart);
    const weekEndStr = getLocalDateString(weekEnd);

    try {
      if (treatmentType === "child" && children.length > 0) {
        const childIds = children.map((child) => child.id);
        const { data: allData, error: fetchError } = await supabase
          .from("child_logs")
          .select(
            "date, time_spent_minutes, daily_goal_at_time, weekly_goal_at_time",
          )
          .in("child_id", childIds)
          .gte("date", weekStartStr)
          .lte("date", weekEndStr)
          .order("date", { ascending: true });

        if (fetchError) throw fetchError;

        const groupedData = new Map<
          string,
          { totalMinutes: number; dailyGoalAtTime?: number | null }
        >();
        (allData || []).forEach((entry: any) => {
          const existing = groupedData.get(entry.date);
          const newTotal =
            (existing?.totalMinutes || 0) + entry.time_spent_minutes;
          // Keep first defined goal (preserves null for rest days)
          const goalAtTime =
            existing?.dailyGoalAtTime !== undefined ? existing.dailyGoalAtTime : entry.daily_goal_at_time;
          groupedData.set(entry.date, {
            totalMinutes: newTotal,
            dailyGoalAtTime: goalAtTime,
          });
        });

        const weeklyLogs: DailyLog[] = Array.from(groupedData.entries()).map(
          ([date, { totalMinutes, dailyGoalAtTime }]) => ({
            date,
            time_spent_minutes: totalMinutes,
            daily_goal_at_time: dailyGoalAtTime,
          }),
        );

        const todayData = groupedData.get(today);
        setCurrentWeekLog({
          date: today,
          time_spent_minutes: todayData?.totalMinutes || 0,
          daily_goal_at_time: todayData?.dailyGoalAtTime,
        });
        setCurrentWeekLogs(weeklyLogs);
      } else {
        const { data: allData, error: fetchError } = await supabase
          .from("daily_logs")
          .select(
            "date, time_spent_minutes, daily_goal_at_time, weekly_goal_at_time",
          )
          .eq("user_id", user.id)
          .gte("date", weekStartStr)
          .lte("date", weekEndStr)
          .order("date", { ascending: true });

        if (fetchError) throw fetchError;

        const groupedData = new Map<
          string,
          { totalMinutes: number; dailyGoalAtTime?: number | null }
        >();
        (allData || []).forEach((entry: any) => {
          const existing = groupedData.get(entry.date);
          const newTotal =
            (existing?.totalMinutes || 0) + entry.time_spent_minutes;
          // Keep first defined goal (preserves null for rest days)
          const goalAtTime =
            existing?.dailyGoalAtTime !== undefined ? existing.dailyGoalAtTime : entry.daily_goal_at_time;
          groupedData.set(entry.date, {
            totalMinutes: newTotal,
            dailyGoalAtTime: goalAtTime,
          });
        });

        const weeklyLogs: DailyLog[] = Array.from(groupedData.entries()).map(
          ([date, { totalMinutes, dailyGoalAtTime }]) => ({
            date,
            time_spent_minutes: totalMinutes,
            daily_goal_at_time: dailyGoalAtTime,
          }),
        );

        const todayData = groupedData.get(today);
        setCurrentWeekLog({
          date: today,
          time_spent_minutes: todayData?.totalMinutes || 0,
          daily_goal_at_time: todayData?.dailyGoalAtTime,
        });
        setCurrentWeekLogs(weeklyLogs);
      }
    } catch (error: any) {
      if (error?.code !== "42501") {
        console.error("Error fetching current week data:", error);
      }
    }
  };

  const fetchStatisticsData = async () => {
    if (!user) return;

    // Apply week offset for viewing previous/future weeks
    const weekStart = new Date();
    weekStart.setDate(
      weekStart.getDate() - weekStart.getDay() + selectedWeekOffset * 7,
    ); // Sunday of selected week
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6); // Saturday of selected week
    const weekStartStr = getLocalDateString(weekStart);
    const weekEndStr = getLocalDateString(weekEnd);

    try {
      // If treatment type is child and children exist, use child_logs, otherwise use legacy daily_logs
      if (treatmentType === "child" && children.length > 0) {
        // Fetch all child logs for this week
        const childIds = children.map((child) => child.id);
        const { data: allData, error: fetchError } = await supabase
          .from("child_logs")
          .select(
            "date, time_spent_minutes, daily_goal_at_time, weekly_goal_at_time",
          )
          .in("child_id", childIds)
          .gte("date", weekStartStr)
          .lte("date", weekEndStr)
          .order("date", { ascending: true });

        if (fetchError) throw fetchError;

        // Group entries by date and sum up the time, keeping historical goal
        const groupedData = new Map<
          string,
          { totalMinutes: number; dailyGoalAtTime?: number | null }
        >();
        (allData || []).forEach((entry: any) => {
          const existing = groupedData.get(entry.date);
          const newTotal =
            (existing?.totalMinutes || 0) + entry.time_spent_minutes;
          // Keep first defined goal (preserves null for rest days)
          const goalAtTime =
            existing?.dailyGoalAtTime !== undefined ? existing.dailyGoalAtTime : entry.daily_goal_at_time;
          groupedData.set(entry.date, {
            totalMinutes: newTotal,
            dailyGoalAtTime: goalAtTime,
          });
        });

        // Create DailyLog objects from grouped data
        const weeklyLogs: DailyLog[] = Array.from(groupedData.entries()).map(
          ([date, { totalMinutes, dailyGoalAtTime }]) => ({
            date,
            time_spent_minutes: totalMinutes,
            daily_goal_at_time: dailyGoalAtTime,
          }),
        );

        // Set weekly logs
        setWeeklyLogs(weeklyLogs);
      } else {
        // Use legacy daily_logs for users without children
        const { data: allData, error: fetchError } = await supabase
          .from("daily_logs")
          .select(
            "date, time_spent_minutes, daily_goal_at_time, weekly_goal_at_time",
          )
          .eq("user_id", user.id)
          .gte("date", weekStartStr)
          .lte("date", weekEndStr)
          .order("date", { ascending: true });

        if (fetchError) throw fetchError;

        // Group entries by date and sum up the time, keeping historical goal
        const groupedData = new Map<
          string,
          { totalMinutes: number; dailyGoalAtTime?: number | null }
        >();
        (allData || []).forEach((entry: any) => {
          const existing = groupedData.get(entry.date);
          const newTotal =
            (existing?.totalMinutes || 0) + entry.time_spent_minutes;
          // Keep first defined goal (preserves null for rest days)
          const goalAtTime =
            existing?.dailyGoalAtTime !== undefined ? existing.dailyGoalAtTime : entry.daily_goal_at_time;
          groupedData.set(entry.date, {
            totalMinutes: newTotal,
            dailyGoalAtTime: goalAtTime,
          });
        });

        // Create DailyLog objects from grouped data
        const weeklyLogs: DailyLog[] = Array.from(groupedData.entries()).map(
          ([date, { totalMinutes, dailyGoalAtTime }]) => ({
            date,
            time_spent_minutes: totalMinutes,
            daily_goal_at_time: dailyGoalAtTime,
          }),
        );

        // Set weekly logs
        setWeeklyLogs(weeklyLogs);
      }
    } catch (error: any) {
      // Don't log RLS policy errors during logout
      if (error?.code !== "42501") {
        console.error("Error fetching statistics data:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserGoals = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("user_goals")
        .select(
          "weekly_goal_minutes, monday_goal_minutes, tuesday_goal_minutes, wednesday_goal_minutes, thursday_goal_minutes, friday_goal_minutes, saturday_goal_minutes, sunday_goal_minutes",
        )
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") throw error;

      if (data) {
        setUserGoals(data);
      } else {
        // Only try to create default goals if we have a valid user session
        // Check if auth session is still valid before attempting to create goals
        const { data: session } = await supabase.auth.getSession();
        if (session?.session?.user?.id === user.id) {
          const defaultDaily = 240;
          const { error: insertError } = await supabase
            .from("user_goals")
            .insert([
              {
                user_id: user.id,
                monday_goal_minutes: defaultDaily,
                tuesday_goal_minutes: defaultDaily,
                wednesday_goal_minutes: defaultDaily,
                thursday_goal_minutes: defaultDaily,
                friday_goal_minutes: defaultDaily,
                saturday_goal_minutes: defaultDaily,
                sunday_goal_minutes: defaultDaily,
                weekly_goal_minutes: defaultDaily * 7,
              },
            ]);

          if (insertError && insertError.code !== "42501") {
            // Don't log RLS policy errors (42501) as they're expected during logout
            console.error("Error creating default goals:", insertError);
          }
        }
      }
    } catch (error: any) {
      // Don't log RLS policy errors during logout
      if (error?.code !== "42501") {
        console.error("Error fetching user goals:", error);
      }
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round((minutes % 60) * 10) / 10; // Round to 1 decimal
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const getDayName = (date: Date) => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return days[date.getDay()];
  };

  // Calculate statistics - use CURRENT week data (not selected week) for goal cards
  const todayMinutes = currentWeekLog?.time_spent_minutes || 0;
  const todayGoal = getGoalForDate(userGoals, getLocalDateString());
  const todayGoalMinutes = todayGoal;
  const dailyPercentage =
    todayGoalMinutes === null || todayGoalMinutes === 0
      ? 100
      : (todayMinutes / todayGoalMinutes) * 100;

  // Calculate individual child's today minutes when a specific child is selected
  const getChildTodayMinutes = (childId: string) => {
    if (localSelectedChildId && localSelectedChildId === childId) {
      // Use selected child logs for individual child data
      const today = getLocalDateString();
      const childTodayLog = selectedChildLogs.find((log) => log.date === today);
      return childTodayLog?.time_spent_minutes || 0;
    }
    return 0;
  };

  const selectedChildTodayMinutes = localSelectedChildId
    ? getChildTodayMinutes(localSelectedChildId)
    : todayMinutes;

  const selectedChildTodayGoalMinutes = (() => {
    if (localSelectedChildId && childStats.length > 0) {
      const goalRow = childGoalsById[localSelectedChildId];
      const todayStr = getLocalDateString();
      const g = goalRow ? getGoalForDate(goalRow, todayStr) : null;
      return g === null ? 0 : (g ?? 0);
    }
    // self / all-children aggregate cards use the self today goal
    return todayGoalMinutes === null ? 0 : (todayGoalMinutes ?? 0);
  })();

  const selectedChildDailyPercentage =
    selectedChildTodayGoalMinutes === 0
      ? 100
      : (selectedChildTodayMinutes /
          Math.max(selectedChildTodayGoalMinutes, 1)) *
        100;

  const weeklyTotal = currentWeekLogs.reduce(
    (sum, log) => sum + log.time_spent_minutes,
    0,
  );
  const computedWeeklyGoal = getWeeklyGoalTotal(userGoals);
  const weeklyPercentage = computedWeeklyGoal > 0
    ? (weeklyTotal / computedWeeklyGoal) * 100
    : 100; // all days are rest days → 100%

  // Create weekly progress for each day
  const weeklyProgress = [];
  const weekStart = new Date();
  weekStart.setDate(
    weekStart.getDate() - weekStart.getDay() + selectedWeekOffset * 7,
  ); // Sunday of selected week
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6); // Saturday of selected week

  for (let i = 0; i < 7; i++) {
    const currentDay = new Date(weekStart);
    currentDay.setDate(weekStart.getDate() + i);
    const dateStr = getLocalDateString(currentDay);

    // Use selected child logs if a specific child is selected, otherwise use combined logs
    const logsToUse = localSelectedChildId ? selectedChildLogs : weeklyLogs;
    const log = logsToUse.find((log) => log.date === dateStr);

    // For today, always use current goal (user may have changed it after logging).
    // For past days, prefer historical goal from the log for accuracy.
    const isToday = dateStr === getLocalDateString();
    let currentGoal: number | null | undefined;
    if (localSelectedChildId && childStats.length > 0) {
      const goalRow = childGoalsById[localSelectedChildId];
      currentGoal = goalRow ? getGoalForDate(goalRow, dateStr) : null;
    } else {
      currentGoal = getGoalForDate(userGoals, dateStr);
    }

    let dailyGoal: number | null | undefined;
    if (isToday && currentGoal !== undefined) {
      dailyGoal = currentGoal;
    } else if (log?.daily_goal_at_time !== undefined) {
      dailyGoal = log.daily_goal_at_time;
    } else {
      dailyGoal = currentGoal;
    }

    // Rest day (NULL) counts as 100% completion
    // Check if it's a rest day even without a log (for today/future dates)
    const isRestDay = dailyGoal === null;
    const dayPercentage = isRestDay
      ? 100 // Rest day = 100% completion
      : log && dailyGoal !== null && dailyGoal > 0
        ? (log.time_spent_minutes / dailyGoal) * 100
        : 0; // No log or invalid goal = 0%

    weeklyProgress.push({
      date: currentDay,
      minutes: log?.time_spent_minutes || 0,
      percentage: dayPercentage,
      isToday: dateStr === getLocalDateString(),
    });
  }

  // Calculate weekly stats for the SELECTED week (not current week)
  const calculateSelectedWeekStats = () => {
    const logsToUse = localSelectedChildId ? selectedChildLogs : weeklyLogs;

    let totalGoalPercentage = 0;
    let daysInWeek = 0;
    let goalsHit = 0;
    let activeDays = 0;

    for (let i = 0; i < 7; i++) {
      const checkDate = new Date(weekStart);
      checkDate.setDate(weekStart.getDate() + i);
      const checkDateStr = getLocalDateString(checkDate);

      const log = logsToUse.find((l) => l.date === checkDateStr);
      const dayTotal = log?.time_spent_minutes || 0;
      // For today, always use current goal; for past days, prefer historical
      const isToday = checkDateStr === getLocalDateString();
      const currentDayGoal = localSelectedChildId
        ? (() => {
            const goalRow = childGoalsById[localSelectedChildId];
            return goalRow ? getGoalForDate(goalRow, checkDateStr) : null;
          })()
        : getGoalForDate(userGoals, checkDateStr);
      const dayGoal =
        isToday && currentDayGoal !== undefined
          ? currentDayGoal
          : log && log.daily_goal_at_time !== undefined
            ? log.daily_goal_at_time
            : currentDayGoal;

      daysInWeek++;
      const dayPercentage =
        dayGoal === null ? 100 : dayGoal > 0 ? (dayTotal / dayGoal) * 100 : 0;
      totalGoalPercentage += dayPercentage;

      if (dayGoal === null || dayTotal >= dayGoal) {
        goalsHit++;
      }
      if (dayTotal > 0) {
        activeDays++;
      }
    }

    return {
      weekly_average_percentage:
        daysInWeek > 0 ? Math.round(totalGoalPercentage / daysInWeek) : 0,
      goals_hit_this_week: goalsHit,
      active_days_this_week: activeDays,
    };
  };

  const selectedWeekStats = calculateSelectedWeekStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading statistics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2
          className={`text-3xl font-bold mb-2 ${
            darkMode ? "text-white" : "text-brand-dark-blue"
          }`}
        >
          {localSelectedChildId && childStats.length > 0
            ? `${
                childStats.find((c) => c.child_id === localSelectedChildId)
                  ?.child_name || "Child"
              }'s Statistics`
            : "Activity Statistics"}
        </h2>
        <p
          className={`text-lg ${darkMode ? "text-gray-300" : "text-gray-600"}`}
        >
          {localSelectedChildId
            ? "Track individual progress and achievements"
            : "Track daily and weekly progress"}
        </p>
      </div>
      {/* Child Selector - Only show if treatment type is child */}
      {treatmentType === "child" && children.length > 0 && (
        <div className="flex justify-center">
          <div className="flex items-center space-x-2">
            <span
              className={`text-sm font-medium ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Child:
            </span>
            <select
              value={localSelectedChildId || children[0]?.id || ""}
              onChange={(e) => {
                setLocalSelectedChildId(e.target.value);
              }}
              className={`px-3 py-1 border rounded-lg text-sm focus:ring-2 focus:ring-brand-cyan focus:border-transparent ${
                darkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
            >
              {children.map((child) => (
                <option key={child.id} value={child.id}>
                  {child.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      {/* Statistics Content - Original structure but child-specific */}
      <div className="space-y-6">
        {/* Daily and Weekly Progress Rings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Daily Progress */}
          <div
            className={`p-8 rounded-2xl ${
              darkMode ? "bg-gray-800" : "bg-white"
            } ${
              darkMode
                ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                : "shadow-lg"
            } text-center`}
          >
            <h3
              className={`text-xl font-semibold mb-6 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {localSelectedChildId && childStats.length > 0
                ? `${
                    childStats.find((c) => c.child_id === localSelectedChildId)
                      ?.child_name || "Child"
                  }'s Daily Goal`
                : "Today's Goal"}
            </h3>

            {
              <CircularProgress
                percentage={
                  selectedChildTodayGoalMinutes === 0
                    ? 100
                    : (selectedChildTodayMinutes /
                        selectedChildTodayGoalMinutes) *
                      100
                }
                size={200}
                strokeWidth={16}
                color={
                  localSelectedChildId && childStats.length > 0
                    ? (selectedChildTodayGoalMinutes === 0
                        ? 100
                        : (selectedChildTodayMinutes /
                            selectedChildTodayGoalMinutes) *
                          100) >= 100
                      ? "#22c55e"
                      : "#10b981"
                    : dailyPercentage >= 100
                      ? "#22c55e"
                      : "#10b981"
                }
                backgroundColor={darkMode ? "#374151" : "#e5e7eb"}
              >
                <div className="text-center">
                  <div
                    className={`text-3xl font-bold ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {localSelectedChildId && childStats.length > 0
                      ? Math.round(
                          selectedChildTodayGoalMinutes === 0
                            ? 100
                            : (selectedChildTodayMinutes /
                                selectedChildTodayGoalMinutes) *
                                100,
                        )
                      : Math.round(dailyPercentage)}
                    %
                  </div>
                  <div
                    className={`text-sm ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {localSelectedChildId && childStats.length > 0
                      ? `${formatTime(
                          selectedChildTodayMinutes,
                        )} / ${formatTime(selectedChildTodayGoalMinutes)}`
                      : todayGoalMinutes === null
                        ? `${formatTime(todayMinutes)} / Rest day`
                        : `${formatTime(todayMinutes)} / ${formatTime(
                            todayGoalMinutes,
                          )}`}
                  </div>
                </div>
              </CircularProgress>
            }

            <div className="mt-6">
              <div
                className={`text-lg font-semibold ${
                  localSelectedChildId && childStats.length > 0
                    ? selectedChildDailyPercentage >= 100
                      ? "text-green-500"
                      : selectedChildDailyPercentage >= 75
                        ? "text-green-400"
                        : selectedChildDailyPercentage >= 50
                          ? "text-yellow-400"
                          : "text-red-400"
                    : dailyPercentage >= 100
                      ? "text-green-500"
                      : dailyPercentage >= 75
                        ? "text-green-400"
                        : dailyPercentage >= 50
                          ? "text-yellow-400"
                          : "text-red-400"
                }`}
              >
                {localSelectedChildId && childStats.length > 0
                  ? selectedChildDailyPercentage >= 100
                    ? "Goal Achieved!"
                    : selectedChildDailyPercentage >= 75
                      ? "Almost There!"
                      : selectedChildDailyPercentage >= 50
                        ? "Good Progress"
                        : "Keep Going!"
                  : dailyPercentage >= 100
                    ? "Goal Achieved!"
                    : dailyPercentage >= 75
                      ? "Almost There!"
                      : dailyPercentage >= 50
                        ? "Good Progress"
                        : "Keep Going!"}
              </div>
            </div>
          </div>

          {/* Weekly Progress */}
          <div
            className={`p-8 rounded-2xl ${
              darkMode ? "bg-gray-800" : "bg-white"
            } ${
              darkMode
                ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                : "shadow-lg"
            } text-center`}
          >
            <h3
              className={`text-xl font-semibold mb-6 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {localSelectedChildId && childStats.length > 0
                ? `${
                    childStats.find((c) => c.child_id === localSelectedChildId)
                      ?.child_name || "Child"
                  }'s Weekly Goal`
                : "This Week's Goal"}
            </h3>

            {
              <CircularProgress
                percentage={
                  localSelectedChildId && childStats.length > 0
                    ? ((childStats.find(
                        (c) => c.child_id === localSelectedChildId,
                      )?.weekly_total || 0) /
                        (childStats.find(
                          (c) => c.child_id === localSelectedChildId,
                        )?.weekly_goal_minutes || 1)) *
                      100
                    : weeklyPercentage
                }
                size={200}
                strokeWidth={16}
                color={
                  localSelectedChildId && childStats.length > 0
                    ? ((childStats.find(
                        (c) => c.child_id === localSelectedChildId,
                      )?.weekly_total || 0) /
                        (childStats.find(
                          (c) => c.child_id === localSelectedChildId,
                        )?.weekly_goal_minutes || 1)) *
                        100 >=
                      100
                      ? "#2563eb"
                      : "#3b82f6"
                    : weeklyPercentage >= 100
                      ? "#2563eb"
                      : "#3b82f6"
                }
                backgroundColor={darkMode ? "#374151" : "#e5e7eb"}
              >
                <div className="text-center">
                  <div
                    className={`text-3xl font-bold ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {localSelectedChildId && childStats.length > 0
                      ? Math.round(
                          ((childStats.find(
                            (c) => c.child_id === localSelectedChildId,
                          )?.weekly_total || 0) /
                            (childStats.find(
                              (c) => c.child_id === localSelectedChildId,
                            )?.weekly_goal_minutes || 1)) *
                            100,
                        )
                      : Math.round(weeklyPercentage)}
                    %
                  </div>
                  <div
                    className={`text-sm ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {localSelectedChildId && childStats.length > 0
                      ? `${formatTime(
                          childStats.find(
                            (c) => c.child_id === localSelectedChildId,
                          )?.weekly_total || 0,
                        )} / ${formatTime(
                          childStats.find(
                            (c) => c.child_id === localSelectedChildId,
                          )?.weekly_goal_minutes || 0,
                        )}`
                      : `${formatTime(weeklyTotal)} / ${formatTime(
                          computedWeeklyGoal,
                        )}`}
                  </div>
                </div>
              </CircularProgress>
            }

            <div className="mt-6">
              <div
                className={`text-lg font-semibold ${
                  localSelectedChildId && childStats.length > 0
                    ? ((childStats.find(
                        (c) => c.child_id === localSelectedChildId,
                      )?.weekly_total || 0) /
                        (childStats.find(
                          (c) => c.child_id === localSelectedChildId,
                        )?.weekly_goal_minutes || 1)) *
                        100 >=
                      100
                      ? "text-blue-500"
                      : ((childStats.find(
                            (c) => c.child_id === localSelectedChildId,
                          )?.weekly_total || 0) /
                            (childStats.find(
                              (c) => c.child_id === localSelectedChildId,
                            )?.weekly_goal_minutes || 1)) *
                            100 >=
                          75
                        ? "text-blue-400"
                        : ((childStats.find(
                              (c) => c.child_id === localSelectedChildId,
                            )?.weekly_total || 0) /
                              (childStats.find(
                                (c) => c.child_id === localSelectedChildId,
                              )?.weekly_goal_minutes || 1)) *
                              100 >=
                            50
                          ? "text-yellow-400"
                          : "text-red-400"
                    : weeklyPercentage >= 100
                      ? "text-blue-500"
                      : weeklyPercentage >= 75
                        ? "text-blue-400"
                        : weeklyPercentage >= 50
                          ? "text-yellow-400"
                          : "text-red-400"
                }`}
              >
                {localSelectedChildId && childStats.length > 0
                  ? ((childStats.find(
                      (c) => c.child_id === localSelectedChildId,
                    )?.weekly_total || 0) /
                      (childStats.find(
                        (c) => c.child_id === localSelectedChildId,
                      )?.weekly_goal_minutes || 1)) *
                      100 >=
                    100
                    ? "Weekly Goal Achieved!"
                    : ((childStats.find(
                          (c) => c.child_id === localSelectedChildId,
                        )?.weekly_total || 0) /
                          (childStats.find(
                            (c) => c.child_id === localSelectedChildId,
                          )?.weekly_goal_minutes || 1)) *
                          100 >=
                        75
                      ? "Almost There!"
                      : ((childStats.find(
                            (c) => c.child_id === localSelectedChildId,
                          )?.weekly_total || 0) /
                            (childStats.find(
                              (c) => c.child_id === localSelectedChildId,
                            )?.weekly_goal_minutes || 1)) *
                            100 >=
                          50
                        ? "Good Progress"
                        : "Push Forward!"
                  : weeklyPercentage >= 100
                    ? "Weekly Goal Met!"
                    : weeklyPercentage >= 75
                      ? "Almost There!"
                      : weeklyPercentage >= 50
                        ? "Good Progress"
                        : "Push Forward!"}
              </div>
            </div>
          </div>
        </div>

        {/* Weekly Overview */}
        <div
          className={`p-6 rounded-2xl ${
            darkMode ? "bg-gray-800" : "bg-white"
          } ${
            darkMode
              ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
              : "shadow-lg"
          }`}
        >
          <h3
            className={`text-xl font-semibold mb-6 ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {selectedWeekOffset === 0
              ? "This Week's Activity"
              : selectedWeekOffset === -1
                ? "Last Week's Activity"
                : `${Math.abs(selectedWeekOffset)} Week${
                    Math.abs(selectedWeekOffset) > 1 ? "s" : ""
                  } Ago Activity`}
          </h3>

          {/* Week Navigation */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setSelectedWeekOffset((prev) => prev - 1)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                darkMode
                  ? "bg-gray-700 hover:bg-gray-600 text-white"
                  : "bg-gray-200 hover:bg-gray-300 text-gray-700"
              }`}
            >
              ← Previous Week
            </button>

            <div
              className={`px-4 py-2 rounded-lg ${
                darkMode
                  ? "bg-gray-700 text-white"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {weekStart.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}{" "}
              -{" "}
              {weekEnd.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </div>

            <button
              onClick={() => setSelectedWeekOffset((prev) => prev + 1)}
              disabled={selectedWeekOffset >= 0}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedWeekOffset >= 0
                  ? "opacity-50 cursor-not-allowed"
                  : darkMode
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
              }`}
            >
              Next Week →
            </button>
          </div>

          {/* Reset to Current Week Button */}
          {selectedWeekOffset !== 0 && (
            <div className="flex justify-center mb-4">
              <button
                onClick={() => setSelectedWeekOffset(0)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  darkMode
                    ? "bg-brand-cyan hover:bg-brand-dark-blue text-white"
                    : "bg-brand-cyan hover:bg-brand-dark-blue text-white"
                }`}
              >
                Reset to Current Week
              </button>
            </div>
          )}

          <div className="grid grid-cols-7 gap-4">
            {weeklyProgress.map((day, index) => (
              <div key={index} className="text-center">
                <div
                  className={`text-sm font-medium mb-3 ${
                    day.isToday
                      ? "text-brand-cyan"
                      : darkMode
                        ? "text-gray-300"
                        : "text-gray-600"
                  }`}
                >
                  {getDayName(day.date)}
                </div>

                <CircularProgress
                  percentage={day.percentage}
                  size={60}
                  strokeWidth={6}
                  color={
                    day.percentage >= 100
                      ? "#22c55e"
                      : day.percentage >= 75
                        ? "#10b981"
                        : day.percentage >= 50
                          ? "#eab308"
                          : day.percentage > 0
                            ? "#f97316"
                            : "#ef4444"
                  }
                  backgroundColor={darkMode ? "#374151" : "#e5e7eb"}
                >
                  <div
                    className={`text-xs font-semibold ${
                      day.isToday
                        ? "text-brand-cyan"
                        : darkMode
                          ? "text-white"
                          : "text-gray-900"
                    }`}
                  >
                    {Math.round(day.percentage)}%
                  </div>
                </CircularProgress>

                <div
                  className={`text-xs mt-2 ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {formatTime(day.minutes)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Statistics Section */}
        <div
          className={`p-6 rounded-2xl ${
            darkMode ? "bg-gray-800" : "bg-white"
          } shadow-lg`}
        >
          <h3
            className={`text-xl font-semibold mb-4 ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {localSelectedChildId && childStats.length > 0
              ? `${
                  childStats.find((c) => c.child_id === localSelectedChildId)
                    ?.child_name || "Child"
                }'s Statistics`
              : "Weekly Statistics"}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Weekly Average % */}
            <div
              className={`p-4 rounded-lg ${
                darkMode ? "bg-gray-700" : "bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className={`text-sm font-medium ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Weekly Average
                </div>
                <svg
                  className="w-5 h-5 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <div
                className={`text-3xl font-bold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {`${selectedWeekStats.weekly_average_percentage}%`}
              </div>
              <div
                className={`text-xs mt-1 ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Avg. goal completion
              </div>
            </div>

            {/* Goals Hit This Week */}
            <div
              className={`p-4 rounded-lg ${
                darkMode ? "bg-gray-700" : "bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className={`text-sm font-medium ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Goals Hit
                </div>
                <svg
                  className="w-5 h-5 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div
                className={`text-3xl font-bold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {selectedWeekStats.goals_hit_this_week}
              </div>
              <div
                className={`text-xs mt-1 ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Days this week
              </div>
            </div>

            {/* Active Days This Week */}
            <div
              className={`p-4 rounded-lg ${
                darkMode ? "bg-gray-700" : "bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className={`text-sm font-medium ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Active Days
                </div>
                <svg
                  className="w-5 h-5 text-yellow-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div
                className={`text-3xl font-bold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {`${selectedWeekStats.active_days_this_week} / 7`}
              </div>
              <div
                className={`text-xs mt-1 ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Days with activity
              </div>
            </div>
          </div>
        </div>

        {/* Goal Information - Only show for users without children */}
        {children.length === 0 && (
          <div
            className={`p-6 rounded-2xl ${
              darkMode ? "bg-gray-800" : "bg-white"
            } shadow-lg`}
          >
            <h3
              className={`text-xl font-semibold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Your Goals
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div
                className={`p-4 rounded-lg ${
                  darkMode ? "bg-gray-700" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-sm font-medium ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Daily Goal
                </div>
                <div
                  className={`text-2xl font-bold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {todayGoalMinutes === null
                    ? "Rest day"
                    : formatTime(todayGoalMinutes)}
                </div>
              </div>

              <div
                className={`p-4 rounded-lg ${
                  darkMode ? "bg-gray-700" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-sm font-medium ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Weekly Goal
                </div>
                <div
                  className={`text-2xl font-bold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {formatTime(userGoals.weekly_goal_minutes)}
                </div>
              </div>
            </div>

            <div
              className={`mt-4 text-sm ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Goals are set by your healthcare provider to support your therapy
              progress.
            </div>
          </div>
        )}
      </div>
      {/* Remove Children's Statistics section entirely - it's redundant with dashboard */}
    </div>
  );
};

export default Statistics;
